import {Navigate, useLocation} from 'react-router-dom'
import {useEffect, useState} from 'react'
import {checkAuth, profileComplete} from '@utils/auth.js'
import useOnboardingStore from '@stores/useOnboardingStore.js'
import {FALLBACK_ALLOWED_ROUTE_PREFIXES, isPathAllowed} from '@onboarding/config.js'
import {normalizePathname} from '@components/Navigation/utils.js'
import {LoaderOverlay} from '@components/ModernLoader.jsx'

const ONBOARDING_BOOTSTRAP_GATE_TIMEOUT_MS = 10_000

export default function RequireAuth({children}) {
  const location = useLocation()
  const pathname = normalizePathname(location.pathname)
  const isAuth = checkAuth()
  const complete = profileComplete()
  const snapshot = useOnboardingStore(state => state.snapshot)
  const loaded = useOnboardingStore(state => state.loaded)
  const [bootstrapTimedOut, setBootstrapTimedOut] = useState(false)

  const hasSnapshot = Boolean(snapshot && typeof snapshot === 'object')
  const allowedRoutePrefixes = hasSnapshot
    ? (Array.isArray(snapshot.allowedRoutePrefixes) ? snapshot.allowedRoutePrefixes : [])
    : [...FALLBACK_ALLOWED_ROUTE_PREFIXES]

  useEffect(() => {
    setBootstrapTimedOut(false)

    if (!isAuth || !complete || hasSnapshot || loaded) {
      return undefined
    }

    const timeoutId = window.setTimeout(() => {
      setBootstrapTimedOut(true)
    }, ONBOARDING_BOOTSTRAP_GATE_TIMEOUT_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [complete, hasSnapshot, isAuth, loaded, pathname])

  if (!isAuth) {
    return <Navigate to="/login" replace state={{from: location}}/>
  }

  if (!complete && pathname !== '/setup-profile') {
    return <Navigate to="/setup-profile" replace/>
  }

  if (complete && !hasSnapshot && !loaded && !bootstrapTimedOut) {
    return <LoaderOverlay/>
  }

  const routeAllowed = isPathAllowed(pathname, allowedRoutePrefixes)
  if (!routeAllowed) {
    const fallbackRoute = hasSnapshot
      ? (snapshot?.nextAction?.route || '/')
      : '/'

    if (normalizePathname(fallbackRoute) === pathname) {
      return <Navigate to="/" replace/>
    }

    return <Navigate to={fallbackRoute} replace/>
  }

  return children
}
